"use client";

import { ProcessSectionStep } from "@/lib/types/section-step";
import { useGSAP } from "@gsap/react";
import {
  AiChemistry02Icon,
  ComputerProgrammingIcon,
  DocumentValidationIcon,
  Rocket02Icon,
  UserGroupIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/all";
import { useRef } from "react";

gsap.registerPlugin(useGSAP, ScrollTrigger);

function ProcessSection() {
  const processSteps: ProcessSectionStep[] = [
    {
      icon: UserGroupIcon,
      title: "1. Let's talk",
      description:
        "Let's sit down (over a video call or in person) and get to know each other better. I want to know the story behind your business, and the goals you want your website or application to achieve.",
    },
    {
      icon: DocumentValidationIcon,
      title: "2. Proposal",
      description:
        "I will create a detailed proposal for your website, outlining what I will deliver, the timeframe and the cost. After we come to an agreement work will commence!",
    },
    {
      icon: ComputerProgrammingIcon,
      title: "3. Design & Development",
      description:
        "I will first design your project to match your vision. Then, I'll develop it using the latest web technologies. You will get regular updates on the progress and can give feedback as we go.",
    },
    {
      icon: AiChemistry02Icon,
      title: "4. Testing",
      description:
        "The project will undergo a thorough testing phase to make sure everything works well. We will test it on different devices and browsers. This checks for performance, compatibility, and accessibility.",
    },
    {
      icon: Rocket02Icon,
      title: "5. Launch",
      description:
        "After you give your final approval, we will launch the website. The website will be hosted on a secure and fast server. This will ensure quick loading times and less downtime. If you'd like to handle hosting yourself, I'm happy to assist with setting that up too.",
    },
  ];

  const sectionRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      gsap.to(progressRef.current, {
        height: "100%",
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "bottom center",
          scrub: 0.5,
        },
      });
      gsap.to(dotRef.current, {
        top: "100%",
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "bottom center",
          scrub: 0.5,
        },
      });
      // Animate icon colours on scroll
      const steps = gsap.utils.toArray<HTMLElement>(".process-step");
      // Get computed rgb colors (oklch from CSS vars isn't interpolatable by GSAP)
      const mutedRgb = getComputedStyle(
        steps[0].querySelector(".process-icon")!,
      ).color;
      const primaryRgb = getComputedStyle(dotRef.current!).backgroundColor;
      steps.forEach((step) => {
        const icon = step.querySelector<HTMLElement>(".process-icon");
        gsap.fromTo(
          icon,
          {
            color: mutedRgb,
          },
          {
            color: primaryRgb,
            duration: 0.5,
            scrollTrigger: {
              trigger: step,
              start: "-=190px",
              end: "bottom",
              toggleActions: "play none none reverse",
            },
          },
        );
      });
    },
    { scope: sectionRef },
  );

  return (
    <div
      ref={sectionRef}
      id="process"
      className="w-full min-h-screen flex flex-col items-center pt-16"
    >
      <div className="w-full md:w-lg xl:w-4xl 2xl:w-5xl">
        <h2 className="text-3xl font-bold mb-5 xl:text-5xl 2xl:text-7xl 2xl:mb-10">
          Our Simple Process
        </h2>
        <div className="flex justify-center gap-9 2xl:gap-15">
          {/* Timeline progress bar */}
          <div className="relative w-1 shrink-0 rounded-full bg-muted">
            {/* Filled portion (primary) */}
            <div
              ref={progressRef}
              className="absolute inset-x-0 top-0 h-0 rounded-full bg-primary"
            />
            {/* Tracking dot */}
            <div
              ref={dotRef}
              className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-primary shadow-md"
            />
          </div>
          {/* Cards */}
          <div className="w-full flex flex-col items-center justify-start gap-6 2xl:gap-10">
            {processSteps.map(({ icon, title, description }, index) => (
              <div
                key={`${title}-${index}`}
                className="process-step flex items-center gap-3 xl:gap-5 border-t-2 border-t-muted pt-6 first:border-t-0 first:pt-3"
              >
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-1 xl:text-2xl">
                    {title}
                  </h3>
                  <p className="text-sm xl:text-lg 2xl:text-xl max-w-[65ch]">
                    {description}
                  </p>
                </div>
                <HugeiconsIcon
                  icon={icon}
                  className="process-icon text-muted size-12 xl:size-50 2xl:size-30"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export { ProcessSection };
